/* The arborist's work on the phone until it syncs (`27` section 6, `02` ->
 * Offline work is kept on the phone, and the token is not): audits,
 * corrections, missed trees and their photos, in IndexedDB, so they survive a
 * closed app and no signal.
 *
 * Each piece of work is one record under an ID generated on the phone, which
 * becomes its `field_audit.id` (`20` section 3.12b), so a resend whose audit
 * already exists writes nothing. Saving the same tree again before it syncs
 * replaces the record and keeps the ID. Nothing here says "synced": synced work
 * is removed from the phone, so everything held here is unsynced.
 *
 * What it deliberately is not: the sync, which is sync.ts's and removes each
 * record the server has taken. Not `localStorage`, which cannot take a photo.
 * Not encrypted: the phone is a managed device instead (`02`).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

const DATABASE = "arborcision-field";
const VERSION = 1;
const STORE = "unsynced";

export type AuditStatus = "validated" | "corrected" | "missed";

export interface Point {
  lat: number;
  lng: number;
}

/* A position as the phone's GPS reported it, with the accuracy it claimed. */
export interface Fix extends Point {
  accuracyM: number | null;
}

/* An arborist's photo, with when and where it was taken: the server stores
 * each (`20` section 3.12b, `field_audit_photo`). */
export interface FieldPhoto {
  blob: Blob;
  takenAt: number;
  location: Fix | null;
}

/* What the arborist recorded about a tree, as the field app's screens set it.
 * A value is present where they changed it; absent agrees with the system.
 * Distances are feet, as the screens set them; the API takes metres (`15`
 * section 1.6), so a sync converts them with lib/format.ts's feetToMeters.
 * No risk: the server scores a tree's, never the phone. No contact flag:
 * contact with the conductor is a clearance of 0 (`27` section 8). */
export interface AuditValues {
  status: AuditStatus;
  species?: string;
  clearance?: number;
  encroach?: number;
  reasons?: string[];
  note?: string;
  repin?: Point;
}

/* Where the arborist placed a missed tree on the phone: a project, and
 * optionally a span of it and a pole at one of the span's ends (`27` section
 * 8). The server checks it and may drop it (`15` section 3.15a). The
 * project's name is for the screens; the sync sends the IDs. */
export interface Placement {
  projectId: string;
  projectName: string;
  spanId: string | null;
  poleId: string | null;
}

/* A tree the system missed, as the arborist recorded it. It has no row of
 * ours to point at, so its audit carries it whole (`27` section 8). Its
 * values are the tree's, never its audit's (`20` section 3.12b). Its height
 * and distances are feet, as AuditValues' are. */
export interface MissedTree extends Point {
  /* Generated on the phone; the missed tree's ID on the server too. */
  id: string;
  /* Null where it was saved unplaced: it then syncs unplaced, and is placed
   * on the web (`27` section 8). */
  placement: Placement | null;
  species: string;
  height: number;
  clearance: number;
  encroach: number;
  photos: FieldPhoto[];
}

export interface UnsyncedAudit {
  /* Generated on the phone; the audit's ID on the server too. */
  id: string;
  /* The tree as the app keys it: our tree's or held missed tree's ID, or
   * the ID of a missed tree recorded here (trees.ts's FieldTree `id`). */
  treeKey: string;
  /* The assignment the tree came to the phone with, from the open list
   * (`15` section 3.15, AR-343). The server requires it on every audit but
   * the one that records a missed tree, which has none (`20` section 3.12b):
   * a verdict on a held missed tree carries its assignment as a tree's does. */
  assignmentId: string | null;
  recordedAt: number;
  audit: AuditValues;
  /* Where the arborist stood, from the fix taken when the audit is saved
   * (`27` section 8, AR-343). The server requires it (`20` section 3.12b),
   * so a record without one is not sent: null is never replaced by a guess. */
  stoodAt: Fix | null;
  /* The arborist's photos of the tree, kept as taken. */
  photos: FieldPhoto[];
  missedTree: MissedTree | null;
  /* Set on disk as the record is sent, and cleared only by an answer saying
   * the server did not take it. Until then the server may hold this version,
   * so no save may replace it (`02` -> A tree being sent cannot be saved
   * again). On the record rather than in the page, so every page sees it. */
  unanswered?: true;
}

/* What saving a tree writes: its record on the phone with `change` applied,
 * or a new record where it has none. The ID, assignment, fix, photos and
 * missed tree carry over unless `change` sets them, so a tree saved again
 * before it syncs keeps the ID that becomes its audit's ID on the server
 * (`20` section 3.12b). `change` is given the existing record to merge into. */
export function recordFor(
  records: readonly UnsyncedAudit[],
  treeKey: string,
  change: (existing: UnsyncedAudit | undefined) => Pick<UnsyncedAudit, "audit"> & Partial<UnsyncedAudit>,
): UnsyncedAudit {
  const existing = records.find((r) => r.treeKey === treeKey);
  return {
    id: existing?.id ?? crypto.randomUUID(),
    treeKey,
    assignmentId: existing?.assignmentId ?? null,
    recordedAt: Date.now(),
    stoodAt: existing?.stoodAt ?? null,
    photos: existing?.photos ?? [],
    missedTree: existing?.missedTree ?? null,
    ...change(existing),
  };
}

/* The audit a save writes: the verdict on screen, never one saved before it.
 * A validation clears an earlier correction, and a correction replaces one,
 * keeping only a pin moved on the map, which the correction form does not
 * show. A missed tree's save merges into what its audit holds, less the
 * tree's values and its pin, which are its MissedTree's (`treeSaved`): the
 * sync route refuses a missed outcome carrying them (`15` section 3.15a). */
export function verdict(prior: AuditValues | undefined, values: AuditValues): AuditValues {
  if (values.status === "validated") return values;
  if (values.status === "corrected") return prior?.repin ? { ...values, repin: prior.repin } : values;
  const { species, clearance, encroach, repin, ...audit } = { ...prior, ...values };
  return audit;
}

/* A missed tree recorded here, with the values a save of its screen changed
 * and its pin where it was moved: the tree's own, as `verdict` keeps them off
 * its audit. */
export function treeSaved(tree: MissedTree, values: AuditValues): MissedTree {
  return {
    ...tree,
    ...(values.species !== undefined ? { species: values.species } : {}),
    ...(values.clearance !== undefined ? { clearance: values.clearance } : {}),
    ...(values.encroach !== undefined ? { encroach: values.encroach } : {}),
    ...values.repin,
  };
}

export interface UnsyncedStore {
  /* Every record, oldest first. */
  all(): Promise<UnsyncedAudit[]>;
  /* Resolves once the record is on disk, and rejects where it is not, or
   * where the record on disk is `unanswered`. */
  put(record: UnsyncedAudit): Promise<void>;
  /* Removes the record where it is still the version saved at `recordedAt`,
   * and resolves to whether it did; rejects where the write fails. */
  remove(id: string, recordedAt: number): Promise<boolean>;
  /* The record as it is on disk, marked `unanswered` there before it is
   * returned where `sendable` says it will be sent; undefined where it is
   * gone, or a round in this page has claimed it already. */
  claim(id: string, sendable: (record: UnsyncedAudit) => boolean): Promise<UnsyncedAudit | undefined>;
  /* Ends this page's claim, and clears `unanswered` where `answered`: an
   * answer said the server did not take the record. */
  release(id: string, answered: boolean): Promise<void>;
}

/* Why a save of an `unanswered` record is refused. The server keeps the
 * first version of an audit it takes (`15` section 3.15a), so a save made
 * after one it may have taken could never reach it. */
export const SENDING = "its audit was sent to Arborcision, and cannot be changed until Arborcision answers";

function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/* Exported for its test: no record `put` accepts makes a write fail once it
 * has begun, so the failure is caused on a transaction of the test's own. */
export function committed(transaction: IDBTransaction): Promise<void> {
  // Abort, not error: a failed request's error reaches the transaction before
  // `transaction.error` is set, and then aborts it with that error.
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("the write was abandoned"));
  });
}

/* `factory` is the browser's `indexedDB`, and a test's own. */
export async function openUnsynced(factory: IDBFactory): Promise<UnsyncedStore> {
  const request = factory.open(DATABASE, VERSION);
  request.onupgradeneeded = () => {
    request.result.createObjectStore(STORE, { keyPath: "id" });
  };
  const db = await result(request);
  // A newer version of the app, opened in another tab or installed by the
  // service worker, cannot upgrade the database while this page holds it open.
  db.onversionchange = () => db.close();
  // The records a round in this page is sending, so a second round leaves
  // them to it. A save is held off by the record's own `unanswered`, read and
  // written inside one transaction, so each page's save meets it.
  const claimed = new Set<string>();
  /* Reads the record under `id` and, in the same transaction, writes what
   * `change` makes of it, nothing where it returns undefined. Resolves to the
   * record read, once the write is done. */
  async function update(
    id: string,
    durability: IDBTransactionDurability,
    change: (record: UnsyncedAudit | undefined, objects: IDBObjectStore) => void,
  ): Promise<UnsyncedAudit | undefined> {
    const transaction = db.transaction(STORE, "readwrite", { durability });
    const written = committed(transaction);
    const objects = transaction.objectStore(STORE);
    const read = objects.get(id) as IDBRequest<UnsyncedAudit | undefined>;
    read.onsuccess = () => change(read.result, objects);
    await written;
    return read.result;
  }
  return {
    async all() {
      const records = await result(
        db.transaction(STORE).objectStore(STORE).getAll() as IDBRequest<UnsyncedAudit[]>,
      );
      return records.sort((a, b) => a.recordedAt - b.recordedAt);
    },
    async put(record) {
      // Strict, so a write is reported done only once it is on disk: this is
      // the only copy of the arborist's work, and a phone can die mid-day.
      const stored = await update(record.id, "strict", (held, objects) => {
        if (!held?.unanswered) objects.put(record);
      });
      if (stored?.unanswered) throw new Error(SENDING);
    },
    async remove(id, recordedAt) {
      // Relaxed: a removal a dying phone loses only sends the record again,
      // which the server answers without writing (`15` section 3.15a).
      const stored = await update(id, "relaxed", (held, objects) => {
        if (held?.recordedAt === recordedAt) objects.delete(id);
      });
      return stored?.recordedAt === recordedAt;
    },
    async claim(id, sendable) {
      if (claimed.has(id)) return undefined;
      claimed.add(id);
      try {
        // Strict: a mark a dying phone lost after the send went out would let
        // a save follow a version the server may hold.
        let marked = false;
        const stored = await update(id, "strict", (held, objects) => {
          if (!held || !sendable(held)) return;
          objects.put({ ...held, unanswered: true });
          marked = true;
        });
        if (!stored) claimed.delete(id);
        return stored && marked ? { ...stored, unanswered: true } : stored;
      } catch (error) {
        claimed.delete(id);
        throw error;
      }
    },
    async release(id, answered) {
      try {
        if (answered) {
          // Relaxed: a clearing a dying phone loses only holds the tree until
          // the next send is answered.
          await update(id, "relaxed", (held, objects) => {
            if (!held?.unanswered) return;
            const { unanswered, ...cleared } = held;
            objects.put(cleared);
          });
        }
      } finally {
        claimed.delete(id);
      }
    },
  };
}

/* Asks the browser to keep this origin's storage when the phone runs short of
 * space. It does not lift WebKit's deletion of storage a site has not used;
 * installing to the home screen does (`27` section 6). Refused or unsupported
 * is not an error: the work is stored either way. */
export async function keepStorage(storage: StorageManager | undefined): Promise<boolean> {
  if (!storage?.persist) return false;
  try {
    return (await storage.persisted()) || (await storage.persist());
  } catch {
    return false;
  }
}

let opened: Promise<UnsyncedStore> | null = null;

/* One connection for the page, and with it one set of claims, so no round in
 * this page sends a record another is sending. A failed open is forgotten, so
 * the next read tries again rather than failing forever. */
export function store(): Promise<UnsyncedStore> {
  if (!opened) {
    void keepStorage(navigator.storage);
    opened = openUnsynced(indexedDB).catch((error: unknown) => {
      opened = null;
      throw error;
    });
  }
  return opened;
}

/* Every record, as it is on disk now. */
export async function unsynced(): Promise<UnsyncedAudit[]> {
  return (await store()).all();
}

const UNSYNCED = ["field", "unsynced"] as const;

/* The work on this phone, and saving to it. `networkMode: "always"` because
 * TanStack Query pauses a query while the phone is offline, which is exactly
 * when this one must run. A save updates the cache with the record it wrote
 * rather than reading the store again, so each photo keeps one Blob, and one
 * object URL (`photoUrl`), for the life of the page. */
export function useUnsyncedWork() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: UNSYNCED,
    queryFn: unsynced,
    networkMode: "always",
    staleTime: Infinity,
  });
  const save = async (record: UnsyncedAudit): Promise<void> => {
    await (await store()).put(record);
    queryClient.setQueryData<UnsyncedAudit[]>(UNSYNCED, (records = []) =>
      records.some((r) => r.id === record.id)
        ? records.map((r) => (r.id === record.id ? record : r))
        : [...records, record],
    );
  };
  /* A record the server has taken leaves the phone, the screens, and with
   * them its photos' URLs, which are the screens' copy's: the version saved at
   * `recordedAt`, and never one saved since. */
  const remove = async (id: string, recordedAt: number): Promise<void> => {
    if (!(await (await store()).remove(id, recordedAt))) return;
    const shown = queryClient.getQueryData<UnsyncedAudit[]>(UNSYNCED)?.find((r) => r.id === id);
    queryClient.setQueryData<UnsyncedAudit[]>(UNSYNCED, (records = []) => records.filter((r) => r.id !== id));
    for (const photo of [...(shown?.photos ?? []), ...(shown?.missedTree?.photos ?? [])]) forgetPhoto(photo);
  };
  return { work: query.data, error: query.error, save, remove };
}

/* A photo is a record on disk and an object URL on screen, and the screens
 * pass the URL around. These map one to the other. A URL is revoked once its
 * photo's record has synced and left the phone (`forgetPhoto`), and not
 * before: until then a screen may show it. */
const urls = new WeakMap<FieldPhoto, string>();
const photos = new Map<string, FieldPhoto>();

export function photoUrl(photo: FieldPhoto): string {
  let url = urls.get(photo);
  if (!url) {
    url = URL.createObjectURL(photo.blob);
    urls.set(photo, url);
    photos.set(url, photo);
  }
  return url;
}

/* The photo behind a URL `photoUrl` made. Any other URL is a photo this page
 * cannot store, and saving it as nothing would lose it without a word. */
export function heldPhoto(url: string): FieldPhoto {
  const photo = photos.get(url);
  if (!photo) throw new Error(`no photo is held for ${url}`);
  return photo;
}

function forgetPhoto(photo: FieldPhoto): void {
  const url = urls.get(photo);
  if (!url) return;
  URL.revokeObjectURL(url);
  urls.delete(photo);
  photos.delete(url);
}
